import Colors from 'enums/Colors';
import ComponentCorrespondentImagem from 'models/componentInteraction/ComponentCorrespondentImagem';
import ComponentCorrespondentImagemAlternativas from 'models/componentInteraction/ComponentCorrespondentImagemAlternativas';
import React, { useEffect, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FaCheckCircle } from 'react-icons/fa';
import { IoIosCloseCircle } from 'react-icons/io';
import Styled from './CorrespondentImagemPublic.styled';
import { bucketS3, caminhoAnexosComponentsInteraction } from 'constants/EnvironmentData';
import { MdMenu } from 'react-icons/md';
import { ButtonHomeV2 } from 'pages/home/components/homeV2/button/ButtonHomeV2.styled';
import { IoReloadCircleSharp } from 'react-icons/io5';
import { OutlineButton } from 'components/UI/Buttons';

const DraggableCard: React.FC<{ onDrop: () => void; card: ComponentCorrespondentImagemAlternativas }> = ({ onDrop, card }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'CARD',
        item: { id: card.codigo },
        end: (item, monitor) => {
            if (monitor.didDrop()) onDrop();
        },
        collect: monitor => ({
            isDragging: !!monitor.isDragging(),
        }),
    }));

    return (
        <div
            ref={drag}
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                border: '1px solid #ccc',
                gap: '1rem',
                height: 60,
                width: 240,
                margin: 16,
                padding: 16,
                backgroundColor: '#fafafa',
                textAlign: 'center',
                cursor: 'grab',
            }}
        >
            <MdMenu />
            <strong>{card.matchText}</strong>
            <div
                style={{
                    position: 'absolute',
                    bottom: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 'bold',
                }}
            ></div>
        </div>
    );
};

const DropZone: React.FC<{
    label: string;
    onDropCard: (dropTarget: number, cardId: number) => void;
    droppedCard?: number;
    card: ComponentCorrespondentImagemAlternativas;
    handleReset: (nextQuest: boolean) => void;
    finished: boolean;
}> = ({ label, onDropCard, droppedCard, card, handleReset, finished }) => {
    const getS3BaseUrl = (): string => {
        return bucketS3() + caminhoAnexosComponentsInteraction();
    };

    const [isThisZone, setThisZone] = useState<boolean>(false);
    const [isCorrect, setCorrect] = useState<boolean>(false);

    const [{ isOver }, drop] = useDrop(() => ({
        accept: 'CARD',
        drop: (item: { id: number }) => {
            onDropCard(card.codigo, item.id);
        },
        collect: monitor => ({
            isOver: !!monitor.isOver(),
        }),
    }));

    useEffect(() => {
        setThisZone(droppedCard != undefined);
        setCorrect(droppedCard === card.codigo);
    }, [droppedCard, label]);

    return (
        <div style={{ position: 'relative' }}>
            {finished && isThisZone && (
                <div
                    style={{
                        position: 'absolute',
                        top: '-16px',
                        left: '100%',
                        transform: 'translateX(-50%)',
                        color: isCorrect ? 'green' : 'red',
                        fontWeight: 'bold',
                        backgroundColor: '#ffffff',
                        borderRadius: '100%',
                        zIndex: 10,
                    }}
                >
                    {isCorrect ? <FaCheckCircle size={32} /> : <IoIosCloseCircle size={32} />}
                </div>
            )}
            <div
                ref={drop}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 200,
                    width: '240px',
                    overflow: 'hidden',
                    margin: 16,
                    backgroundColor: isOver ? '#f0f0f0' : '#fafafa',
                    border: `4px solid ${isThisZone && finished ? (isCorrect ? 'green' : 'red') : '#ccc'}`,
                    position: 'relative',
                    textAlign: 'center',
                }}
            >
                <img src={getS3BaseUrl() + card.filepath} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />

                {isThisZone && (
                    <>
                        |
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'absolute',
                                border: '1px solid dashed',
                                width: '100%',
                                height: '60px',
                                backgroundColor: '#F8F8F8',
                                bottom: 0,
                            }}
                        >
                            {label}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

interface IComponentCorrespondentImagemPublic {
    dados: ComponentCorrespondentImagem;
}

const ComponentCorrespondentImagemPublic: React.FC<IComponentCorrespondentImagemPublic> = ({ dados }) => {
    const [correspondentImagem] = useState<ComponentCorrespondentImagem>(dados);
    const [droppedZone, setDroppedZone] = useState<Record<number, number>>({});

    const [alternativasImagens, setAlternativasImagem] = useState<ComponentCorrespondentImagemAlternativas[]>([]);
    const [alternativasMatch, setAlternativasMatch] = useState<ComponentCorrespondentImagemAlternativas[]>([]);

    const [associetes, setAssocietes] = useState<boolean>(false);
    const [finished, setFinished] = useState<boolean>(false);

    const handleDrop = (zone: number, cardId: number) => {
        setDroppedZone(prev => ({ ...prev, [zone]: cardId }));
    };

    const getAlternativaMatch = (idAlt: number) => {
        //Verificar se existe a key id no dropped
        //Pegar o valor do id relacionado e retornar o matchText

        for (const [key, value] of Object.entries(droppedZone)) {
            if (parseInt(key) === idAlt) {
                const component = correspondentImagem.alternativas.find(a => a.codigo === value);
                if (component) return component.matchText;
            }
        }

        return '';
    };

    const validateAllAssocietes = () => {
        if (correspondentImagem.alternativas.length === Object.entries(droppedZone).length) {
            setAssocietes(true);
        }
    };

    const handleFinish = () => setFinished(true);

    const handleNewGame = () => {
        setDroppedZone({});
        setAssocietes(false);
        setFinished(false);
    };

    useEffect(() => validateAllAssocietes(), [droppedZone]);

    return (
        <DndProvider backend={HTML5Backend}>
            <Styled.Container>
                <Styled.Title dangerouslySetInnerHTML={{ __html: correspondentImagem.title }} />
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '240px 240px 240px',
                        gap: '10px',
                    }}
                >
                    {correspondentImagem &&
                        correspondentImagem.alternativas &&
                        correspondentImagem.alternativas.length > 0 &&
                        correspondentImagem.alternativas
                            .sort((a, b) => a.ordemFilepath - b.ordemFilepath)
                            .map((alt, index) => (
                                <div key={alt.codigo}>
                                    <DropZone
                                        label={getAlternativaMatch(alt.codigo)}
                                        onDropCard={(zoneId, cardId) => handleDrop(zoneId, cardId)}
                                        droppedCard={droppedZone[alt.codigo]}
                                        card={alt}
                                        handleReset={next => {
                                            setDroppedZone({});
                                        }}
                                        finished={finished}
                                    />
                                </div>
                            ))}
                </div>
                {!finished && (
                    <div style={{ display: 'grid', gridTemplateColumns: '240px 240px 240px', gap: '10px' }}>
                        {correspondentImagem &&
                            correspondentImagem.alternativas &&
                            correspondentImagem.alternativas.length > 0 &&
                            correspondentImagem.alternativas
                                .sort((a, b) => a.ordemMatch - b.ordemMatch)
                                .filter((al) => !Object.values(droppedZone).includes(al.codigo))
                                .map((alt, index) => (
                                    <div key={alt.codigo}>
                                        <DraggableCard onDrop={() => {}} card={alt} />
                                    </div>
                                ))}
                    </div>
                )}

                {!finished ? (
                    <ButtonHomeV2
                        style={{
                            width: '240px',
                            margin: '20px',
                            marginLeft: '48px',
                            border: finished ? '' : 'none',
                            backgroundColor: associetes ? '' : '#cccccc',
                        }}
                        disabled={!associetes}
                        onClick={handleFinish}
                    >
                        Concluir exercício
                    </ButtonHomeV2>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
                        <FaCheckCircle size={48} color={Colors.BrightGreen} />
                        <Styled.TitleFeed>Parabéns, você concluiu a tarefa!</Styled.TitleFeed>
                        <p dangerouslySetInnerHTML={{__html: correspondentImagem.feedback}} />
                        <OutlineButton style={{ width: '220px' }} onClick={handleNewGame}>
                            Tentar novamente
                        </OutlineButton>
                    </div>
                )}
            </Styled.Container>
        </DndProvider>
    );
};

export default ComponentCorrespondentImagemPublic;
